import type {
  ResearchBoard,
  ResearchBoardClient,
  ResearchBoardSyncResult,
} from './index';

type Fetcher = typeof fetch;

const defaultFetcher: Fetcher = (input, init) => globalThis.fetch(input, init);

type ClientOptions = {
  basePath?: string;
  fetcher?: Fetcher;
};

type ResearchBoardRequestError = Error & { status: number };

type ResearchBoardSyncPayload = {
  pinned?: unknown;
  selected_board_ids?: unknown;
  selectedBoardIds?: unknown;
};

const isNotFoundError = (error: unknown): error is ResearchBoardRequestError =>
  error instanceof Error &&
  'status' in error &&
  typeof error.status === 'number' &&
  error.status === 404;

const parseResponse = async (response: Response) => {
  const payload = await response.json().catch((): null => null);
  if (!response.ok) {
    const error = new Error(
      payload?.error || 'Saved Products request failed'
    ) as ResearchBoardRequestError;
    error.status = response.status;
    throw error;
  }
  return payload;
};

const parseSyncResult = (payload: unknown): ResearchBoardSyncResult => {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Saved Products returned an invalid sync response');
  }

  const { pinned, selected_board_ids, selectedBoardIds } =
    payload as ResearchBoardSyncPayload;
  const selectedIds = selectedBoardIds ?? selected_board_ids;

  if (
    !(
      Array.isArray(selectedIds) &&
      selectedIds.every((selectedId) => typeof selectedId === 'string')
    ) ||
    typeof pinned !== 'boolean'
  ) {
    throw new Error('Saved Products returned an invalid sync response');
  }

  return {
    pinned,
    selectedBoardIds: selectedIds,
  };
};

export const createResearchBoardClient = ({
  basePath = '/api/research-board',
  fetcher = defaultFetcher,
}: ClientOptions = {}): ResearchBoardClient => {
  const request = async (path: string, options: RequestInit = {}) => {
    const response = await fetcher(`${basePath}${path}`, {
      ...options,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
    return parseResponse(response);
  };

  return {
    async listBoards(productCanonicalId: string): Promise<ResearchBoard[]> {
      const params = new URLSearchParams({
        product_canonical_id: productCanonicalId,
      });
      try {
        const payload = await request(`/boards?${params.toString()}`);
        return payload.boards || [];
      } catch (error) {
        if (isNotFoundError(error)) return [];
        throw error;
      }
    },
    async createBoard({ productCanonicalId, source, title }) {
      const payload = await request('/boards', {
        body: JSON.stringify({
          product_canonical_id: productCanonicalId,
          source,
          title,
        }),
        method: 'POST',
      });
      return payload.board;
    },
    async syncProduct({
      productCanonicalId,
      selectedBoardIds,
      source,
    }): Promise<ResearchBoardSyncResult> {
      const payload = await request('/boards/sync', {
        body: JSON.stringify({
          product_canonical_id: productCanonicalId,
          selected_board_uuids: selectedBoardIds,
          source,
        }),
        method: 'POST',
      });
      return parseSyncResult(payload);
    },
  };
};
